/**
 * Fetches a single match by id for the Match Details screen.
 * API returns hydrated odds (home, draw, away, markets); screen shows market sections from odds.markets.
 */

import { useState, useEffect } from 'react';
import { fetchMatchById } from '../helpers/createApi';
import type { MatchDetailsPayload } from '../helpers/createApi';

export interface UseMatchDetailsResult {
  match: MatchDetailsPayload | null;
  loading: boolean;
  error: boolean;
}

export function useMatchDetails(gameId: string): UseMatchDetailsResult {
  const [match, setMatch] = useState<MatchDetailsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!gameId || typeof gameId !== 'string' || gameId.trim().length === 0) {
      setMatch(null);
      setLoading(false);
      setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetchMatchById(gameId)
      .then((data) => {
        if (!cancelled) {
          setMatch(data);
          setError(data == null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMatch(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  return { match, loading, error };
}
